import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { serverLink } from "../../../resources/url";
import DataLoader from "../../common/dataLoader/dataLoader";
import DataTable from "../../common/data-table/data-table";
import { showAlert } from "../../common/sweetalert/sweetalert";
import { toast } from "react-toastify";
import "./system-backup.css";

// ── Common cron presets ─────────────────────────────────────────────────────
const CRON_PRESETS = [
    { label: "Every day at 2 AM",       expr: "0 2 * * *"   },
    { label: "Every day at midnight",   expr: "0 0 * * *"   },
    { label: "Every 6 hours",           expr: "0 */6 * * *" },
    { label: "Every Sunday at 2 AM",    expr: "0 2 * * 0"   },
    { label: "1st of month at 2 AM",    expr: "0 2 1 * *"   },
    { label: "Every 30 minutes",        expr: "*/30 * * * *" },
];

function SystemBackup(props) {
    const staffID = props.loginData[0]?.StaffID ?? "";

    // ── Backup state ─────────────────────────────────────────────────────────
    const [isLoading,       setIsLoading]       = useState(true);
    const [isRunning,       setIsRunning]        = useState(false);
    const [isScheduling,    setIsScheduling]     = useState(false);
    const [showModal,       setShowModal]        = useState(false);
    const [backupList,      setBackupList]       = useState([]);
    const [scheduledDate,   setScheduledDate]    = useState("");

    // ── Cron job state ───────────────────────────────────────────────────────
    const [cronList,        setCronList]         = useState([]);
    const [showCronModal,   setShowCronModal]    = useState(false);
    const [isSavingCron,    setIsSavingCron]     = useState(false);
    const [cronForm,        setCronForm]         = useState({ Label: "", CronExpression: "" });

    const tableHeaders = ["S/N", "File Name", "Size", "Type", "Status", "Backup Date", "Actions"];
    const cronHeaders  = ["S/N", "Label", "Cron Expression", "Status", "Created", "Actions"];

    // ── Minimum datetime for the schedule picker (now + 1 min) ──────────────
    const getMinDateTime = () => {
        const d = new Date(Date.now() + 60_000);
        return d.toISOString().slice(0, 16);
    };

    // ── Derived stats ────────────────────────────────────────────────────────
    const totalBackups    = backupList.length;
    const completedCount  = backupList.filter(b => b.Status === "Completed").length;
    const scheduledCount  = backupList.filter(b => b.Status === "Scheduled").length;
    const lastBackup      = backupList.find(b => b.Status === "Completed");

    // ── Fetch all backup records ─────────────────────────────────────────────
    const fetchBackups = async () => {
        try {
            const res = await axios.get(`${serverLink}staff/backup/list`);
            setBackupList(Array.isArray(res.data) ? res.data : []);
        } catch {
            showAlert("NETWORK ERROR", "Could not load backup records. Please check your connection.", "error");
        } finally {
            setIsLoading(false);
        }
    };

    // ── Fetch cron jobs ──────────────────────────────────────────────────────
    const fetchCronJobs = async () => {
        try {
            const res = await axios.get(`${serverLink}staff/backup/crons`);
            setCronList(Array.isArray(res.data) ? res.data : []);
        } catch {
            // silently fail — not critical on first load
        }
    };

    useEffect(() => {
        fetchBackups();
        fetchCronJobs();
    }, []);

    // ── Run Backup Now ───────────────────────────────────────────────────────
    const handleRunBackup = async () => {
        setIsRunning(true);
        try {
            const res = await axios.post(`${serverLink}staff/backup/run`, { InsertedBy: staffID });
            if (res.data.message === "success") {
                toast.success(`✅ Backup completed! File: ${res.data.fileName} (${res.data.fileSize})`);
                await fetchBackups();
            } else {
                showAlert("BACKUP FAILED", res.data.error ?? "Something went wrong.", "error");
            }
        } catch (e) {
            showAlert("ERROR", e.response?.data?.error ?? "Backup failed. Ensure mysqldump is available.", "error");
        } finally {
            setIsRunning(false);
        }
    };

    // ── Schedule Backup ──────────────────────────────────────────────────────
    const handleSchedule = async () => {
        if (!scheduledDate) {
            toast.warning("Please select a future date and time.");
            return;
        }
        setIsScheduling(true);
        try {
            const res = await axios.post(`${serverLink}staff/backup/schedule`, {
                InsertedBy:    staffID,
                ScheduledDate: scheduledDate,
            });
            if (res.data.message === "success") {
                toast.success("⏰ Backup scheduled successfully!");
                setShowModal(false);
                setScheduledDate("");
                await fetchBackups();
            } else {
                showAlert("SCHEDULE FAILED", res.data.error ?? "Could not schedule backup.", "error");
            }
        } catch (e) {
            showAlert("ERROR", e.response?.data?.error ?? "Failed to schedule backup.", "error");
        } finally {
            setIsScheduling(false);
        }
    };

    // ── Download Backup ──────────────────────────────────────────────────────
    const handleDownload = (entryID, fileName) => {
        const url = `${serverLink}staff/backup/download/${entryID}`;
        const a   = document.createElement("a");
        a.href     = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    // ── Delete Backup ────────────────────────────────────────────────────────
    const handleDelete = async (entryID, fileName) => {
        const confirm = await showAlert(
            "Delete Backup",
            `Are you sure you want to delete "${fileName}"? This action cannot be undone.`,
            "warning"
        );
        if (!confirm) return;

        try {
            const res = await axios.delete(`${serverLink}staff/backup/delete/${entryID}`);
            if (res.data.message === "success") {
                toast.success("Backup record and file deleted.");
                await fetchBackups();
            } else {
                showAlert("ERROR", "Failed to delete backup.", "error");
            }
        } catch {
            showAlert("NETWORK ERROR", "Could not delete backup. Please try again.", "error");
        }
    };

    // ── Save new cron job ────────────────────────────────────────────────────
    const handleSaveCron = async () => {
        const { Label, CronExpression } = cronForm;
        if (!Label.trim() || !CronExpression.trim()) {
            toast.warning("Please provide a label and a cron expression.");
            return;
        }
        setIsSavingCron(true);
        try {
            const res = await axios.post(`${serverLink}staff/backup/crons`, {
                Label:          Label.trim(),
                CronExpression: CronExpression.trim(),
                InsertedBy:     staffID,
            });
            if (res.data.message === "success") {
                toast.success("🕐 Cron job created and running!");
                setShowCronModal(false);
                setCronForm({ Label: "", CronExpression: "" });
                await fetchCronJobs();
            } else {
                showAlert("ERROR", res.data.error ?? "Could not create cron job.", "error");
            }
        } catch (e) {
            showAlert("ERROR", e.response?.data?.message ?? "Failed to create cron job.", "error");
        } finally {
            setIsSavingCron(false);
        }
    };

    // ── Toggle cron job ──────────────────────────────────────────────────────
    const handleToggleCron = async (cronID, label, currentState) => {
        const action = currentState ? "disable" : "enable";
        const confirm = await showAlert(
            `${currentState ? "Disable" : "Enable"} Cron Job`,
            `Are you sure you want to ${action} "${label}"?`,
            "warning"
        );
        if (!confirm) return;
        try {
            const res = await axios.put(`${serverLink}staff/backup/crons/${cronID}/toggle`);
            if (res.data.message === "success") {
                toast.success(`Cron job ${action}d successfully.`);
                await fetchCronJobs();
            } else {
                showAlert("ERROR", "Toggle failed.", "error");
            }
        } catch {
            showAlert("NETWORK ERROR", "Could not toggle cron job.", "error");
        }
    };

    // ── Delete cron job ──────────────────────────────────────────────────────
    const handleDeleteCron = async (cronID, label) => {
        const confirm = await showAlert(
            "Delete Cron Job",
            `Are you sure you want to permanently delete "${label}"?`,
            "warning"
        );
        if (!confirm) return;
        try {
            const res = await axios.delete(`${serverLink}staff/backup/crons/${cronID}`);
            if (res.data.message === "success") {
                toast.success("Cron job deleted.");
                await fetchCronJobs();
            } else {
                showAlert("ERROR", "Could not delete cron job.", "error");
            }
        } catch {
            showAlert("NETWORK ERROR", "Could not delete cron job.", "error");
        }
    };

    // ── Format date ─────────────────────────────────────────────────────────
    const fmtDate = (str) => {
        if (!str || str === "pending") return "—";
        const d = new Date(str);
        if (isNaN(d)) return str;
        return d.toLocaleString("en-GB", {
            day: "2-digit", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    };

    // ── Status badge ────────────────────────────────────────────────────────
    const StatusBadge = ({ status }) => {
        const icons = { Completed: "✓", Scheduled: "⏰", Failed: "✗", Scheduled_pending: "⋯" };
        const s     = (status ?? "").toLowerCase();
        return (
            <span className={`sb-badge ${s}`}>
                {icons[status] ?? "•"} {status}
            </span>
        );
    };

    // ── Table rows ───────────────────────────────────────────────────────────
    const renderTableRows = () => {
        if (!backupList.length) return null;
        return backupList.map((item, index) => (
            <tr key={item.EntryID}>
                <td className="text-xs font-weight-bold">{index + 1}</td>
                <td className="text-xs font-weight-bold">
                    <span style={{ fontSize: "12px", fontFamily: "monospace", color: "#4c51bf" }}>
                        {item.FileName === "pending" ? "⏳ Awaiting schedule…" : item.FileName}
                    </span>
                </td>
                <td className="text-xs font-weight-bold">
                    <strong>{item.FileSize === "0 B" && item.Status !== "Completed" ? "—" : item.FileSize}</strong>
                </td>
                <td className="text-xs font-weight-bold">
                    <span className={`sb-type-badge ${item.BackupType?.toLowerCase()}`}>
                        {item.BackupType === "Manual" ? "⚡" : item.BackupType === "Cron" ? "🔁" : "📅"} {item.BackupType}
                    </span>
                </td>
                <td className="text-xs font-weight-bold">
                    <StatusBadge status={item.Status} />
                </td>
                <td className="text-xs font-weight-bold">
                    {item.Status === "Scheduled"
                        ? <span style={{ color: "#d97706", fontSize: "12px" }}>📅 {fmtDate(item.ScheduledDate)}</span>
                        : fmtDate(item.InsertedDate)
                    }
                </td>
                <td className="text-xs font-weight-bold">
                    <div style={{ display: "flex", gap: "6px" }}>
                        {item.Status === "Completed" && (
                            <button
                                className="sb-action-btn download"
                                title="Download backup"
                                onClick={() => handleDownload(item.EntryID, item.FileName)}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                    <polyline points="7 10 12 15 17 10"/>
                                    <line x1="12" y1="15" x2="12" y2="3"/>
                                </svg>
                            </button>
                        )}
                        <button
                            className="sb-action-btn delete"
                            title="Delete backup"
                            onClick={() => handleDelete(item.EntryID, item.FileName)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                <path d="M10 11v6"/><path d="M14 11v6"/>
                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                            </svg>
                        </button>
                    </div>
                </td>
            </tr>
        ));
    };

    // ── Cron table rows ──────────────────────────────────────────────────────
    const renderCronRows = () => {
        if (!cronList.length) return null;
        return cronList.map((item, index) => (
            <tr key={item.CronID}>
                <td className="text-xs font-weight-bold">{index + 1}</td>
                <td className="text-xs font-weight-bold">
                    <span style={{ fontWeight: 600, color: "#1a1f3c" }}>{item.Label}</span>
                </td>
                <td className="text-xs font-weight-bold">
                    <code className="sb-cron-expr">{item.CronExpression}</code>
                </td>
                <td className="text-xs font-weight-bold">
                    {item.IsActive
                        ? <span className="sb-badge completed">▶ Active</span>
                        : <span className="sb-badge failed">⏸ Paused</span>
                    }
                </td>
                <td className="text-xs font-weight-bold">{fmtDate(item.CreatedAt)}</td>
                <td className="text-xs font-weight-bold">
                    <div style={{ display: "flex", gap: "6px" }}>
                        <button
                            className={`sb-action-btn ${item.IsActive ? "pause" : "resume"}`}
                            title={item.IsActive ? "Pause cron job" : "Resume cron job"}
                            onClick={() => handleToggleCron(item.CronID, item.Label, item.IsActive)}
                        >
                            {item.IsActive
                                ? <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                                : <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                            }
                        </button>
                        <button
                            className="sb-action-btn delete"
                            title="Delete cron job"
                            onClick={() => handleDeleteCron(item.CronID, item.Label)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                <path d="M10 11v6"/><path d="M14 11v6"/>
                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                            </svg>
                        </button>
                    </div>
                </td>
            </tr>
        ));
    };

    // ── Render ───────────────────────────────────────────────────────────────
    if (isLoading) {
        return (
            <div className="container-xl" style={{ height: "700px" }}>
                <div className="page-header d-print-none"><DataLoader /></div>
            </div>
        );
    }

    return (
        <>
            {/* ── One-shot Schedule Modal ──────────────────────────────────── */}
            {showModal && (
                <div className="sb-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
                    <div className="sb-modal">
                        <div style={{ fontSize: "36px", marginBottom: "12px" }}>📅</div>
                        <h4 className="sb-modal-title">Schedule Backup</h4>
                        <p className="sb-modal-subtitle">
                            Set a future date and time for an automatic database backup.
                            The backup will run automatically at the specified time.
                        </p>

                        <label htmlFor="scheduledDate">Backup Date &amp; Time</label>
                        <input
                            id="scheduledDate"
                            type="datetime-local"
                            min={getMinDateTime()}
                            value={scheduledDate}
                            onChange={(e) => setScheduledDate(e.target.value)}
                        />

                        <div className="sb-modal-footer">
                            <button className="sb-modal-cancel" onClick={() => { setShowModal(false); setScheduledDate(""); }}>
                                Cancel
                            </button>
                            <button
                                className="sb-btn-schedule"
                                onClick={handleSchedule}
                                disabled={isScheduling || !scheduledDate}
                                style={{ opacity: isScheduling || !scheduledDate ? 0.6 : 1 }}
                            >
                                {isScheduling ? <><span className="sb-spinner" /> Scheduling…</> : <>📅 Schedule Backup</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Cron Job Modal ───────────────────────────────────────────── */}
            {showCronModal && (
                <div className="sb-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowCronModal(false); }}>
                    <div className="sb-modal" style={{ maxWidth: "520px" }}>
                        <div style={{ fontSize: "36px", marginBottom: "12px" }}>🔁</div>
                        <h4 className="sb-modal-title">Add Recurring Cron Job</h4>
                        <p className="sb-modal-subtitle">
                            Set a recurring schedule using a cron expression. The backup will fire automatically every time the schedule triggers.
                        </p>

                        {/* Quick presets */}
                        <label style={{ marginBottom: "8px" }}>Quick Presets</label>
                        <div className="sb-cron-presets">
                            {CRON_PRESETS.map((p) => (
                                <button
                                    key={p.expr}
                                    className={`sb-preset-chip ${cronForm.CronExpression === p.expr ? "active" : ""}`}
                                    onClick={() => setCronForm(f => ({ ...f, CronExpression: p.expr }))}
                                    type="button"
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>

                        <label htmlFor="cronLabel" style={{ marginTop: "18px" }}>Job Label</label>
                        <input
                            id="cronLabel"
                            type="text"
                            className="sb-text-input"
                            placeholder="e.g. Daily 2 AM Backup"
                            value={cronForm.Label}
                            onChange={(e) => setCronForm(f => ({ ...f, Label: e.target.value }))}
                        />

                        <label htmlFor="cronExpr" style={{ marginTop: "14px" }}>Cron Expression</label>
                        <input
                            id="cronExpr"
                            type="text"
                            className="sb-text-input"
                            placeholder="e.g.  0 2 * * *"
                            value={cronForm.CronExpression}
                            onChange={(e) => setCronForm(f => ({ ...f, CronExpression: e.target.value }))}
                        />
                        <p className="sb-cron-hint">
                            Format: <code>minute hour day month weekday</code>&nbsp;·&nbsp;
                            <a href="https://crontab.guru" target="_blank" rel="noreferrer">crontab.guru ↗</a>
                        </p>

                        <div className="sb-modal-footer">
                            <button className="sb-modal-cancel" onClick={() => { setShowCronModal(false); setCronForm({ Label: "", CronExpression: "" }); }}>
                                Cancel
                            </button>
                            <button
                                className="sb-btn-cron"
                                onClick={handleSaveCron}
                                disabled={isSavingCron || !cronForm.Label || !cronForm.CronExpression}
                                style={{ opacity: isSavingCron || !cronForm.Label || !cronForm.CronExpression ? 0.6 : 1 }}
                            >
                                {isSavingCron ? <><span className="sb-spinner" /> Saving…</> : <>🔁 Save Cron Job</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="container-xl">
                {/* ── Hero Banner ─────────────────────────────────────────── */}
                <div className="sb-hero">
                    <div style={{ position: "relative", zIndex: 1 }}>
                        <div className="sb-hero-icon">🗄️</div>
                        <h1 className="sb-hero-title">System Backup</h1>
                        <p className="sb-hero-subtitle">
                            Manage and automate your database backups — run instantly, schedule once, or set recurring cron jobs.
                        </p>
                        {lastBackup && (
                            <div style={{ marginTop: "12px" }}>
                                <span className="sb-last-info">
                                    🕐 Last backup: {fmtDate(lastBackup.InsertedDate)} &nbsp;·&nbsp; {lastBackup.FileSize}
                                </span>
                            </div>
                        )}
                    </div>
                    {/* Action buttons */}
                    <div style={{ position: "relative", zIndex: 1, marginTop: "24px", display: "flex", gap: "12px", flexWrap: "wrap" }}>
                        <button
                            id="runBackupBtn"
                            className="sb-btn-run"
                            onClick={handleRunBackup}
                            disabled={isRunning}
                        >
                            {isRunning
                                ? <><span className="sb-spinner" /> Running Backup…</>
                                : <>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17"
                                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                        <line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                                    </svg>
                                    Run Backup Now
                                </>
                            }
                        </button>
                        <button
                            id="scheduleBackupBtn"
                            className="sb-btn-schedule"
                            onClick={() => setShowModal(true)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17"
                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                <line x1="16" y1="2" x2="16" y2="6"/>
                                <line x1="8" y1="2" x2="8" y2="6"/>
                                <line x1="3" y1="10" x2="21" y2="10"/>
                                <line x1="12" y1="14" x2="12" y2="18"/>
                                <line x1="10" y1="16" x2="14" y2="16"/>
                            </svg>
                            Schedule Backup
                        </button>
                        <button
                            id="addCronJobBtn"
                            className="sb-btn-cron"
                            onClick={() => setShowCronModal(true)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17"
                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/>
                                <polyline points="12 6 12 12 16 14"/>
                            </svg>
                            Manage Cron Jobs
                            {cronList.length > 0 && (
                                <span className="sb-cron-badge-count">{cronList.length}</span>
                            )}
                        </button>
                    </div>
                </div>

                {/* ── Running Progress Banner ──────────────────────────────── */}
                {isRunning && (
                    <div className="sb-progress-banner">
                        <div className="sb-spinner" style={{ width: 24, height: 24, borderWidth: 3 }} />
                        <div>
                            <div className="sb-progress-text">Backup in Progress…</div>
                            <div className="sb-progress-sub">Running mysqldump and compressing files. This may take a moment.</div>
                        </div>
                    </div>
                )}

                {/* ── Stats Cards ──────────────────────────────────────────── */}
                <div className="row g-3 mb-4">
                    <div className="col-6 col-md-3">
                        <div className="sb-stat-card total">
                            <span className="sb-stat-icon">📦</span>
                            <p className="sb-stat-value">{totalBackups}</p>
                            <p className="sb-stat-label">Total Backups</p>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="sb-stat-card completed">
                            <span className="sb-stat-icon">✅</span>
                            <p className="sb-stat-value">{completedCount}</p>
                            <p className="sb-stat-label">Completed</p>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="sb-stat-card scheduled-card">
                            <span className="sb-stat-icon">⏰</span>
                            <p className="sb-stat-value">{scheduledCount}</p>
                            <p className="sb-stat-label">Scheduled</p>
                        </div>
                    </div>
                    <div className="col-6 col-md-3">
                        <div className="sb-stat-card size-card">
                            <span className="sb-stat-icon">💾</span>
                            <p className="sb-stat-value" style={{ fontSize: "20px" }}>
                                {lastBackup ? lastBackup.FileSize : "—"}
                            </p>
                            <p className="sb-stat-label">Last Backup Size</p>
                        </div>
                    </div>
                </div>

                {/* ── Backup Records Table ──────────────────────────────────── */}
                <div className="sb-main-card" style={{ marginBottom: "28px" }}>
                    <div className="sb-card-header">
                        <h5 className="sb-card-header-title">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                viewBox="0 0 24 24" fill="none" stroke="#667eea"
                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                <polyline points="14 2 14 8 20 8"/>
                                <line x1="16" y1="13" x2="8" y2="13"/>
                                <line x1="16" y1="17" x2="8" y2="17"/>
                                <polyline points="10 9 9 9 8 9"/>
                            </svg>
                            Backup History
                        </h5>
                        <span style={{ fontSize: "13px", color: "#6b7280" }}>
                            {totalBackups} record{totalBackups !== 1 ? "s" : ""}
                        </span>
                    </div>
                    <div className="sb-card-body">
                        {backupList.length === 0 ? (
                            <div className="sb-empty">
                                <span className="sb-empty-icon">🗄️</span>
                                <p className="sb-empty-title">No Backups Yet</p>
                                <p className="sb-empty-desc">
                                    Click <strong>Run Backup Now</strong> to create your first database backup.
                                </p>
                            </div>
                        ) : (
                            <DataTable
                                tableID="systemBackupTable"
                                header={tableHeaders}
                                body={renderTableRows()}
                                title="System Backup History"
                            />
                        )}
                    </div>
                </div>

                {/* ── Cron Jobs Table ───────────────────────────────────────── */}
                <div className="sb-main-card">
                    <div className="sb-card-header">
                        <h5 className="sb-card-header-title">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                viewBox="0 0 24 24" fill="none" stroke="#f59e0b"
                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/>
                                <polyline points="12 6 12 12 16 14"/>
                            </svg>
                            Recurring Cron Jobs
                        </h5>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <span style={{ fontSize: "13px", color: "#6b7280" }}>
                                {cronList.length} job{cronList.length !== 1 ? "s" : ""}
                                {cronList.filter(c => c.IsActive).length > 0 &&
                                    <> · <span style={{ color: "#059669" }}>{cronList.filter(c => c.IsActive).length} active</span></>
                                }
                            </span>
                            <button className="sb-btn-cron sb-btn-cron-sm" onClick={() => setShowCronModal(true)}>
                                + Add Job
                            </button>
                        </div>
                    </div>
                    <div className="sb-card-body">
                        {cronList.length === 0 ? (
                            <div className="sb-empty">
                                <span className="sb-empty-icon">🔁</span>
                                <p className="sb-empty-title">No Cron Jobs Yet</p>
                                <p className="sb-empty-desc">
                                    Click <strong>+ Add Job</strong> to set up a recurring automatic backup.
                                </p>
                            </div>
                        ) : (
                            <DataTable
                                tableID="cronJobsTable"
                                header={cronHeaders}
                                body={renderCronRows()}
                                title="Recurring Cron Backup Jobs"
                            />
                        )}
                    </div>
                </div>

                {/* Bottom spacing */}
                <div style={{ height: "40px" }} />
            </div>
        </>
    );
}

const mapStateToProps = (state) => ({
    loginData: state.loginDetails,
});

export default connect(mapStateToProps, null)(SystemBackup);
